import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserGroupIcon, UserIcon, HomeIcon, HeartIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { StatusBadge } from '../../../shared/components/badges/StatusBadge';
import AddChildModal from '../../../shared/components/ui/modals/AddChildModal';
import ParentExpandRow from '../components/ParentExpandRow';
import TabbedForm from '../../../shared/components/forms/FormTabs';
import { ProfileUpload } from '../../users/components/profileUpload';
import { PatientService } from '../../../shared/services/patientService';
import { getPath } from '../../../app/router/routes';

// Generic loader for dropdowns from API with fallback
const loadDropdownOptions = async (key, fallback = []) => {
  try {
    const res = await PatientService.getDropdowns();

    const raw =
      res?.data?.data?.[key] ||
      res?.data?.[key] ||
      res?.[key] ||
      [];

    return raw.map(item => ({
      value: item.value,
      label: item.label
    }));
  } catch (error) {
    console.warn(`Failed to load ${key} from API. Using fallback`, error);
    return fallback;
  }
};

// Options loaders
export const getGenderOptions = () => loadDropdownOptions('genders');
export const getBloodGroupOptions = () => loadDropdownOptions('bloodGroups');
export const getStatusOptions = () => loadDropdownOptions('patientStatuses');
export const getRelationOptions = () => loadDropdownOptions('relations');
export const getAllergiesOptions = () => loadDropdownOptions('allergies');

export const parentRecordConfig = {
  title: 'Parent Records',
  endpoint: 'admin/parents',
  storageKey: 'parentRecords',
  icon: UserGroupIcon,
  useUrlNavigation: true,
  basePath: '/admin/patients',

  columns: [
    {
      key: 'parentName',
      label: 'Parent/Guardian Name',
      render: (item) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0 h-10 w-10">
            {item.profilePicture ? (
              <img 
                src={item.profilePicture} 
                alt={item.parentName} 
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center">
                <span className="text-lg">👨‍👩‍👧‍👦</span>
              </div>
            )}
          </div>
          <div>
           {item.parentName}
          </div>
        </div>
      )
    },
       {
      key: 'age',
      label: 'Age',
      render: (item) => 
     
          item.age
   
      
    },


{
  key: 'childrenCount',
  label: 'Children',
  render: (item) => item?._count?.children ?? 0
},


    {
      key: 'relationWithChild',
      label: 'Relation',
      render: (item) => 
   
          item.relationWithChild || '—'
      
    },
 
    {
      key: 'lastVisitDate',
      label: 'Last Visit',
      render: (item) => (
        <span>
          {
            item.lastVisitDate
              ? new Date(item.lastVisitDate).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                })
              : '-'
          }
        </span>
      )
    },

      {
          key: 'status',
          label: 'Status',
          render: (item) => <StatusBadge status={item.status} />
        },
  ],

  formFields: [

 {
    name: "profilePicture",
    label: "Profile Picture",
    accept: 'image/*',
    placeholder: 'Upload parent profile picture (optional)',
    section: 'personal',
    fullWidth: true,
    customRender: (field, value, onChange, error) => (
      <ProfileUpload field={field} value={value} onChange={onChange} error={error} />
    ),
    // optional
  },
    {
      name: 'parentName',
      label: 'Parent/Guardian Name',
      type: 'text',
      required: true,
      placeholder: 'Enter parent/guardian name',
      section: 'personal'
    },
    {
      name: 'age',
      label: 'Age',
      type: 'number',
      required: true,
      placeholder: 'Enter age',
      min: 0,
      max: 120,
      section: 'personal'
    },
    {
      name: 'gender',
      label: 'Gender',
      type: 'select',
      required: true,
      section: 'personal',
      optionsLoader: getGenderOptions
    },
    {
      name: 'dateOfBirth',
      label: 'Date of Birth',
      type: 'date',
      required: true,
      section: 'personal'
    },
    {
      name: 'bloodGroup',
      label: 'Blood Group',
      type: 'select',
      section: 'personal',
      optionsLoader: getBloodGroupOptions
    },
    // {
    //   name: 'numberOfChildren',
    //   label: 'Number of Children',
    //   type: 'number',
    //   required: true,
    //   placeholder: 'Enter number of children',
    //   min: 1,
    //   max: 10,
    //   section: 'personal'
    // },
    {
      name: 'relationWithChild',
      label: 'Relation with Child',
      type: 'select',
      required: true,
      placeholder: 'Select relation',
      section: 'personal',
      optionsLoader: getRelationOptions
    },

    // Contact & Address Information
    {
      name: 'phone',
      label: 'Phone Number',
      type: 'tel',
      required: true,
      placeholder: '+91-XXXXXXXXXX',
      section: 'contact'
    },
    {
      name: 'email',
      label: 'Email Address',
      type: 'email',
      required: true,
      placeholder: 'parent@email.com',
      section: 'contact'
    },
    {
      name: 'address',
      label: 'Address',
      type: 'textarea',
      required: true,
      placeholder: 'Complete address...',
      rows: 2,
      fullWidth: true,
      section: 'contact'
    },
    {
      name: 'city',
      label: 'City',
      type: 'text',
      required: true,
      placeholder: 'Enter city',
      section: 'contact'
    },
    {
      name: 'state',
      label: 'State',
      type: 'text',
      required: true,
      placeholder: 'Enter state',
      section: 'contact'
    },
    {
      name: 'pincode',
      label: 'Pincode',
      type: 'text',
      required: true,
      placeholder: 'Enter pincode',
      maxLength: 6,
      section: 'contact'
    },
    {
      name: 'emergencyContactName',
      label: 'Emergency Contact Name',
      type: 'text',
      placeholder: 'Emergency contact person',
      section: 'contact'
    },
    {
      name: 'emergencyContactPhone',
      label: 'Emergency Contact Phone',
      type: 'tel',
      placeholder: '+91-XXXXXXXXXX',
      section: 'contact'
    },




    // Medical & Notes
    {
      name: 'registrationDate',
      label: 'Registration Date',
      type: 'date',
      required: true,
      default: new Date().toISOString().split('T')[0],
      section: 'medical'
    },
    {
      name: 'registrationTime',
      label: 'Registration Time',
      type: 'time',
      required: true,
      default: new Date().toTimeString().split(' ')[0].substring(0, 5),
      section: 'medical'
    },
    {
      name: 'lastVisitDate',
      label: 'Last Visit Date',
      type: 'date',
      section: 'medical'
    },
    {
      name: 'lastVisitTime',
      label: 'Last Visit Time',
      type: 'time',
      section: 'medical'
    },

    {
      name: 'medicalHistory',
      label: 'Medical History',
      type: 'textarea',
      placeholder: 'Any relevant medical conditions, allergies, etc...',
      rows: 3,
      fullWidth: true,
      section: 'medical'
    },
    {
      name: 'notes',
      label: 'Additional Notes',
      type: 'textarea',
      placeholder: 'Any additional notes about the parent...',
      rows: 3,
      fullWidth: true,
      section: 'medical'
    }
  ],

  searchFields: ['parentName', 'email', 'phone', 'relationship'],


customActions: [
//   {
//     icon: "👶",
//     tooltip: "Add Child",
//     className: "text-green-600 hover:bg-green-50",
//     onClick: (item) => {
//       // Redirect to /admin/child/add with parentId as query param
// window.location.href = `/admin/children/add?parentId=${item.id}&parentName=${encodeURIComponent(item.parentName)}`;
//     },
//   },
{
  icon: "👶",
  tooltip: "Add Child",
  className: "text-green-600 hover:bg-green-50",
  onClick: (item) => {
    window.location.href =
      `${getPath('CHILDREN')}/add/${item.id}`;
  },
}
],
  formState: { activeTab: 'personal' },



customHandlers: {
  // Custom onView handler to fetch parent data and children data
  onView: async ({ id }) => {
    // Fetch parent data
    const parentRes = await PatientService.getById(id);
    const parentData = parentRes?.data?.data || parentRes?.data || parentRes;
    console.log('Parent data fetched:', parentData);

    // Fetch children data using the dedicated endpoint
    try {
      const childrenRes = await PatientService.getChildrenByParentId(id);
      console.log('Children API response:', childrenRes);
      // Response format: { status: "success", data: [...], pagination: {...} }
      const childrenData = childrenRes?.data || [];
      console.log('Children data extracted:', childrenData);
      // Merge children into parent data
      parentData.children = Array.isArray(childrenData) ? childrenData : [];
      console.log('Parent data with children:', parentData);
    } catch (error) {
      console.error('Failed to fetch children for parent:', error);
      parentData.children = [];
    }

    console.log('Final parent data with children:', parentData);
    return parentData;
  },
 
  renderFormSections: (formFields, formData, handleFormChange, mode, renderFormField, activeTab, setActiveTab) => {
    const tabsConfig = {
      personal: { label: 'Personal Info' },
      contact: { label: 'Contact & Address' },
      medical: { label: 'Medical & Notes' }
    };
    return <TabbedForm
      formFields={formFields}
      formData={formData}
      handleFormChange={handleFormChange}
      renderFormField={renderFormField}
      tabsConfig={tabsConfig}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />;
  },

renderExpandedRow: (item, onUpdate) => (
  <ParentExpandRow item={item} onUpdate={onUpdate} viewMode="expanded" />
),
  renderViewDetails: (item) => (
    <ParentExpandRow item={item} viewMode="show" />
  ),
  
  processFormData: (formData, mode) => {
    const processedData = {
      ...formData,
      age: parseInt(formData.age) || 0,
      // numberOfChildren: parseInt(formData.numberOfChildren, 10) || 0,
      email: formData.email?.toLowerCase(),
      gender: formData.gender?.toUpperCase(),
      bloodGroup: formData.bloodGroup?.toUpperCase().replace(/\+/g, '_POSITIVE').replace(/-/g, '_NEGATIVE'),
    };
    
    // Process children array
    if (formData.children && Array.isArray(formData.children)) {
      processedData.children = formData.children.map(child => ({
        ...child,
        gender: child.gender?.toUpperCase(),
        bloodGroup: child.bloodGroup?.toUpperCase().replace(/\+/g, '_POSITIVE').replace(/-/g, '_NEGATIVE'),
        allergies: typeof child.allergies === 'string' ? child.allergies.split(',').map(a => a.trim()).filter(Boolean) : (child.allergies || []),
        medicalConditions: typeof child.medicalConditions === 'string' ? child.medicalConditions.split(',').map(m => m.trim()).filter(Boolean) : (child.medicalConditions || []),
        weightKg: parseFloat(child.weightKg) || 0,
        heightCm: parseFloat(child.heightCm) || 0
      }));
    }

    // Combine date and time fields for createdAt
    if (formData.registrationDate && formData.registrationTime) {
      const date = new Date(formData.registrationDate);
      const [hours, minutes] = formData.registrationTime.split(':');
      date.setHours(parseInt(hours), parseInt(minutes));
      processedData.createdAt = date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      }) + ', ' + date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }

    // Combine last visit date and time
    if (formData.lastVisitDate && formData.lastVisitTime) {
      const date = new Date(formData.lastVisitDate);
      const [hours, minutes] = formData.lastVisitTime.split(':');
      date.setHours(parseInt(hours), parseInt(minutes));
      processedData.lastVisit = date.toISOString().split('T')[0];
    } else if (formData.lastVisitDate) {
      processedData.lastVisit = formData.lastVisitDate;
    }

    return processedData;
  },
  
  validateFormData: (formData) => {
    const errors = {};
    
    if (formData.gender && !['MALE', 'FEMALE', 'OTHER'].includes(formData.gender.toUpperCase())) {
      errors.gender = 'Gender must be MALE, FEMALE, or OTHER';
    }
    
    return errors;
  }
}
};

